import type { TRPCOutputs } from '~/utils/trpc'
import { HOUR_MS, formatDayWithYear, formatElapsed, formatTimeAgo } from '~/utils/date'

export type StationLeakV0Status = TRPCOutputs['ops']['stations']['leakV0Status']

interface LeakV0Summary {
  facts: string[]
  color: 'gray' | 'amber'
}

// The detector ticks hourly: a shorter window cries wolf over a couple of missed runs, a longer one
// lets a wedged detector hide for more than a day.
const STALE_BUCKET_HOURS = 24

const hoursBehind = (lastBucketAt: string, lastReadingAt: string): number =>
  (new Date(lastReadingAt).getTime() - new Date(lastBucketAt).getTime()) / HOUR_MS

interface LeakV0Warmup {
  active: boolean
  label: string
}

export const leakV0Warmup = (status: StationLeakV0Status, now: Date = new Date()): LeakV0Warmup => {
  if (status.warmupUntil === null) return { active: false, label: 'not set' }

  const active = new Date(status.warmupUntil).getTime() > now.getTime()
  return { active, label: `${active ? 'warming up until' : 'completed'} ${formatDayWithYear(status.warmupUntil)}` }
}

export const summarizeLeakV0Status = (status: StationLeakV0Status, now: Date = new Date()): LeakV0Summary => {
  if (!status.enrolled) return { facts: ['Not enrolled', status.leakProvider], color: 'gray' }

  const warmup = leakV0Warmup(status, now)
  if (warmup.active) return { facts: ['Enrolled', warmup.label], color: 'gray' }

  // A null `lastReadingAt` is a provisioning gap, not a detector failure — the meter never reported,
  // so the detector had nothing to run on.
  if (!status.lastBucketAt) {
    return {
      facts: ['Enrolled', status.lastReadingAt ? 'no output yet' : 'no meter readings yet'],
      color: 'gray'
    }
  }

  // Measured against the last reading, never against now: the detector skips a station whose meter
  // has gone quiet without writing a bucket, so an upstream outage would flag the whole fleet.
  if (status.lastReadingAt && hoursBehind(status.lastBucketAt, status.lastReadingAt) >= STALE_BUCKET_HOURS) {
    return { facts: ['Enrolled', `no buckets in ${formatElapsed(status.lastBucketAt, now)}`], color: 'amber' }
  }

  return {
    facts: [status.leakProvider, status.currentState, `last bucket ${formatTimeAgo(status.lastBucketAt, now)}`],
    color: 'gray'
  }
}
